import { create } from "zustand";
import { config } from "../config";
import { prepareHeaders } from "../configuration/tokenHandling/prepareHeaders";

interface Payload {
  assetIds: string[];
}

interface Metrics {
  max: number;
  value: number;
}

interface RelationshipScore {
  connectionEngagement: Metrics;
  financialHealth: Metrics;
  operationalDiscipline: Metrics;
  relationship: Metrics;
}

interface ScoreState {
  data: RelationshipScore | null;
  loading: boolean;
  error: string | null;
  setLoading: (status: boolean) => void;
  postScore: (payload: Payload) => Promise<void>;
  clearScoreData: () => void;
}

const InitialState = {connectionEngagement: { max: 250, value: 0 }, financialHealth: { max: 450, value: 0 }, relationship: { max: 1000, value: 0 }, operationalDiscipline: { max: 300, value: 0} }

const getHeaders = (): Headers => {
  let headers = new Headers();
  return prepareHeaders(headers);
};

const useRelationshipStore = create<ScoreState>((set) => ({
  data: null,
  loading: false,
  error: null,
  setLoading: (status: boolean) => {
    set({data: InitialState, loading: status})
  },
  postScore: async (payload) => {
    set({ loading: true, error: null }); // Set loading to true, clear previous errors
    try {
      const response = await fetch(
        `${config.backend.SCORES_SERVICE}/v2/relationships`,
        {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        set({ data: InitialState, loading: false });
        throw new Error(errorData.message || errorData || "An error occurred"); // Throw error for catch block
      }

      const scoreData: RelationshipScore = await response.json();
      set({ data: scoreData, loading: false });
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    } catch (err: any) {
      set({ data: InitialState, error: err.message, loading: false });
      console.error("Error relationship score:", err); // Keep the console log
    }
  },
  clearScoreData: () => set({ data: InitialState, error: null, loading: false }),
}));

export default useRelationshipStore;
